import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { getSocietyName } from '../../utils/brandingUtils';
import nivaasLogo from '../../assets/nivaas-logo.png';
import { getImageUrl } from '../../services/api';
import axios from 'axios';

// Import both CSS files
import './Navbar.css';
import '../../styles/SuperadminNavbar.css';

// Dynamic CSS import based on user role
const NavbarCSS = ({ children, currentUserRole }) => {
  useEffect(() => {
    // Dynamically import the appropriate CSS
    let styleElement = null;

    if (currentUserRole === 'superadmin') {
      import('../../styles/SuperadminNavbar.css')
        .then(() => {
          // console.log('Loaded superadmin navbar styles');
        })
        .catch(err => {
          console.error('Failed to load superadmin styles:', err);
        });
    } else {
      import('./Navbar.css')
        .then(() => {
          // console.log('Loaded regular navbar styles');
        })
        .catch(err => {
          console.error('Failed to load regular styles:', err);
        });
    }

    return () => {
      // Clean up if needed
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, [currentUserRole]);

  return children;
}

const Navbar = () => {
  const { currentUser, logout } = useContext(AuthContext);
  const { settings, loadSettings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgDomain } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [logoError, setLogoError] = useState(false);
  const themeCssRef = useRef(null);
  const initialLoadDoneRef = useRef(false);
  const [defaultsettings, setDefaultsettings] = useState([]);

  useEffect(() => {
    const fetchDefaultSettings = async () => {
      try {
        const settings_response = await axios.get('/api/saas/settings/default-settings');
        setDefaultsettings(settings_response.data);
      } catch (err) {
        console.log('error');
      }
    };

    fetchDefaultSettings();
  }, []);

  // Force reload settings to ensure we have the latest, but only once on mount
  useEffect(() => {
    // Only load settings if not maintenance role to avoid unauthorized API calls
    if (!initialLoadDoneRef.current && (!currentUser || currentUser.role !== 'maintenance')) {
      loadSettings();
      initialLoadDoneRef.current = true;
    }
  }, [loadSettings, currentUser]);

  // Only apply theme CSS for non-superadmin users
  useEffect(() => {
    if (currentUser?.role !== 'superadmin') {
      // Apply standard theme CSS
      if (!themeCssRef.current) {
        themeCssRef.current = document.createElement('style');
        themeCssRef.current.id = 'navbar-theme-styles';
        document.head.appendChild(themeCssRef.current);
      }

      // Custom colors for non-superadmin
      const primaryColor = settings.theme_primary_color || '#0d6efd';

      // Use !important to ensure styles are applied consistently
      themeCssRef.current.innerHTML = `
        :root {
          --sidebar-active-bg-color: ${settings.sidebar_active_bg_color || settings.theme_primary_color || '#0d6efd'} !important;
          --sidebar-active-text-color: ${settings.sidebar_active_text_color || '#ffffff'} !important;
          --sidebar-bg-color: ${settings.sidebar_bg_color || '#ffffff'} !important;
          --sidebar-text-color: ${settings.sidebar_text_color || '#212529'} !important;
          --sidebar-hover-bg-color: ${settings.sidebar_hover_bg_color || '#f8f9fa'} !important;
          --sidebar-hover-text-color: ${settings.sidebar_hover_text_color || settings.theme_primary_color || '#0d6efd'} !important;
          --primary-color: ${primaryColor} !important;
        }
        .sidebar-navigation .logo-image {
          width: ${(settings.logo_width ? settings.logo_width : 130) + 'px !important'};
        }
      `;
    } else {
      // For superadmin, remove the standard theme CSS if it exists
      if (themeCssRef.current) {
        themeCssRef.current.remove();
        themeCssRef.current = null;
      }
    }

    return () => {
      if (themeCssRef.current) {
        themeCssRef.current.remove();
        themeCssRef.current = null;
      }
    };
  }, [settings, currentUser?.role]);

  // Close sidebar on mobile when changing routes
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    // Set initial state based on screen size
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Close sidebar on route change on mobile
  useEffect(() => {
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  // Fix logo URL if needed
  const getLogoUrl = useCallback((url) => {
    if (!url) {
      // console.log('No logo URL provided');
      return null;
    }

    // Never show any unrelated logo
    if (!currentUser || !currentUser.organization_id) {
      // console.log('Not showing logo because no organization context exists');
      return null;
    }

    // Only use logos that match the organization ID for relative paths
    if (url.startsWith('/uploads') && !url.includes(`/${currentUser.organization_id}/`)) {
      // console.log(`Not using logo URL ${url} because it doesn't match organization ID ${currentUser.organization_id}`);
      return null;
    }

    // Use the centralized getImageUrl function which handles CORS issues
    const processedUrl = getImageUrl(url);
    // console.log(`Processed logo URL: ${url} -> ${processedUrl}`);
    return processedUrl;
  }, [currentUser]);

  // Preload logo to check if it's valid
  useEffect(() => {
    // Reset logo state when settings change
    setLogoError(false);

    // Always prefer showing the society name if setting is enabled
    if (settings.show_society_name === 'true' || settings.show_society_name === true) {
      // console.log('Show society name setting is enabled, not loading logo');
      setLogoError(true);
      return;
    }

    if (settings.logo_url) {
      const logoUrl = getLogoUrl(settings.logo_url);

      // Skip loading if getLogoUrl returned null
      if (!logoUrl) {
        // console.log('Logo URL resolved to null, not loading logo');
        setLogoError(true);
        return;
      }

      // console.log(`Attempting to preload logo from: ${logoUrl}`);
      const img = new Image();
      img.onload = () => {
        // console.log('Logo loaded successfully');
      };
      img.onerror = () => {
        // console.log('Logo failed to load during preload check:', e);
        setLogoError(true);
      };
      img.crossOrigin = 'anonymous'; // Try to avoid CORS issues
      img.src = logoUrl;
    } else {
      // console.log('No logo URL provided in settings');
      setLogoError(true);
    }

    // Cleanup function
    return () => {
      // Reset logo state when component unmounts or settings change
      setLogoError(false);
    };
  }, [settings.logo_url, settings.society_name, settings.show_society_name, currentUser?.organization_id, getLogoUrl]);

  // Get superadmin menu items
  const getSuperadminMenuItems = () => {
    return [
      { path: '/superadmin/dashboard', label: 'Dashboard', icon: 'fas fa-tachometer-alt', className: 'superadmin-menu-item' },
      { path: '/superadmin/tenants', label: 'Organizations', icon: 'fas fa-building', className: 'superadmin-menu-item' },
      { path: '/superadmin/plans', label: 'Subscription Plans', icon: 'fas fa-tags', className: 'superadmin-menu-item' },
      { path: '/superadmin/subscriptions', label: 'Subscriptions', icon: 'fas fa-credit-card', className: 'superadmin-menu-item' },
      { path: '/superadmin/testimonials', label: 'Testimonials', icon: 'fas fa-star', className: 'superadmin-menu-item' },
      { path: '/superadmin/support-tickets', label: 'Support Tickets', icon: 'fas fa-headset', className: 'superadmin-menu-item' },
      { path: '/superadmin/settings', label: 'Platform Settings', icon: 'fas fa-cogs', className: 'superadmin-menu-item' },
      { path: '/superadmin/switch-user', label: 'Switch User', icon: 'fas fa-exchange-alt', className: 'superadmin-menu-item' }
    ];
  };

  // Get admin menu items
  const getAdminMenuItems = () => {
    const prefix = orgDomain ? `/${orgDomain}` : '';
    return [
      { path: `${prefix}/admin/dashboard`, label: 'Dashboard', icon: 'fas fa-tachometer-alt' },
      { path: `${prefix}/admin/units`, label: 'Units', icon: 'fas fa-home' },
      { path: `${prefix}/admin/visitors`, label: 'Visitors', icon: 'fas fa-users' },
      { path: `${prefix}/admin/complaints`, label: 'Complaints / Suggestions', icon: 'fas fa-exclamation-circle' },
      { path: `${prefix}/admin/financials`, label: 'Financials', icon: 'fas fa-chart-line' },
      { path: `${prefix}/admin/corpus-fund`, label: 'Corpus Fund', icon: 'fas fa-piggy-bank' },
      { path: `${prefix}/admin/activity`, label: 'Activity', icon: 'fas fa-list-alt' },
      { path: `${prefix}/admin/reports`, label: 'Reports', icon: 'fas fa-chart-bar' },
      { path: `${prefix}/admin/rules`, label: 'Rules', icon: 'fas fa-gavel' },
      { path: `${prefix}/admin/announcements`, label: 'Announcements', icon: 'fas fa-bullhorn' },
      { path: `${prefix}/directory`, label: 'Directory', icon: 'fas fa-address-book' },
      { path: `${prefix}/admin/users`, label: 'Users', icon: 'fas fa-user-friends' },
      { path: `${prefix}/admin/membership`, label: 'Membership', icon: 'fas fa-id-card' },
      { path: `${prefix}/admin/support-tickets`, label: 'Support Tickets', icon: 'fas fa-headset' },
      { path: `${prefix}/admin/settings`, label: 'Settings', icon: 'fas fa-cogs' }
    ];
  };

  // Get owner menu items
  const getOwnerMenuItems = () => {
    const prefix = orgDomain ? `/${orgDomain}` : '';
    return [
      { path: `${prefix}/owner/dashboard`, label: 'Dashboard', icon: 'fas fa-tachometer-alt', forceReload: true },
      { path: `${prefix}/owner/my-units`, label: `My Units`, icon: 'fas fa-home' },
      { path: `${prefix}/owner/complaints`, label: 'Complaints / Suggestions', icon: 'fas fa-exclamation-circle' },
      { path: `${prefix}/owner/payments`, label: 'Payments', icon: 'fas fa-credit-card' },
      { path: `${prefix}/owner/visitors`, label: 'Visitors', icon: 'fas fa-users' },
      { path: `${prefix}/owner/vehicles`, label: 'Vehicles', icon: 'fas fa-car' },

      { path: `${prefix}/owner/rules`, label: 'Rules', icon: 'fas fa-gavel' },
      { path: `${prefix}/owner/announcements`, label: 'Announcements', icon: 'fas fa-bullhorn' },
      { path: `${prefix}/directory`, label: 'Directory', icon: 'fas fa-address-book' }
    ];
  };

  // Get guard menu items
  const getGuardMenuItems = () => {
    const prefix = orgDomain ? `/${orgDomain}` : '';
    return [
      { path: `${prefix}/guard/dashboard`, icon: 'fas fa-tachometer-alt', label: 'Dashboard' },
      { path: `${prefix}/guard/visitors`, icon: 'fas fa-users', label: 'Visitors' },
      { path: `${prefix}/guard/visitor-search`, icon: 'fas fa-search', label: 'Search' },
      { path: `${prefix}/guard/villas`, icon: 'fas fa-home', label: 'Units' },
      { path: `${prefix}/guard/vehicles`, icon: 'fas fa-car', label: 'Vehicles' },
      { path: `${prefix}/guard/rules`, icon: 'fas fa-gavel', label: 'Rules' },
      { path: `${prefix}/guard/announcements`, icon: 'fas fa-bullhorn', label: 'Announcements' }
    ];
  };

  const getMaintenanceMenuItems = () => [
    { path: '/maintenance/dashboard', icon: 'fas fa-tachometer-alt', label: 'Dashboard' },
    { path: '/maintenance/complaints', icon: 'fas fa-tools', label: 'Complaints / Suggestions' },
    { path: '/maintenance/rules', icon: 'fas fa-gavel', label: 'Rules' },
    { path: '/maintenance/announcements', icon: 'fas fa-bullhorn', label: 'Announcements' }
  ];

  // Get menu items based on user role
  const getMenuItems = () => {
    if (!currentUser) return [];

    switch (currentUser.role) {
      case 'superadmin':
        return getSuperadminMenuItems();
      case 'admin':
        return getAdminMenuItems();
      case 'owner':
        return getOwnerMenuItems();
      case 'guard':
        return getGuardMenuItems();
      case 'maintenance':
        return getMaintenanceMenuItems();
      default:
        return [];
    }
  };

  // Initialize dropdown with Bootstrap directly
  useEffect(() => {
    // Make sure Bootstrap is loaded
    const checkBootstrap = () => {
      if (typeof window !== 'undefined') {
        if (window.bootstrap) {
          return true;
        } else {
          // Create and append Bootstrap script if not present
          const bootstrapScript = document.createElement('script');
          bootstrapScript.src = 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/js/bootstrap.bundle.min.js';
          bootstrapScript.integrity = 'sha384-geWF76RCwLtnZ8qwWowPQNguL3RmwHVBC9FhGdlKrxdiJJigb/j/68SIy3Te4Bkz';
          bootstrapScript.crossOrigin = 'anonymous';
          document.body.appendChild(bootstrapScript);
        }
      }
      return false;
    };

    // Try to initialize immediately
    if (!checkBootstrap()) {
      // If not loaded, try again after a delay
      setTimeout(checkBootstrap, 1000);
    }
  }, []);

  // Determine dashboard URL based on user role
  const getDashboardUrl = () => {
    if (!currentUser) return '/login';

    const prefix = orgDomain ? `/${orgDomain}` : '';
    let dashboardUrl = '/login';
    switch (currentUser.role) {
      case 'superadmin':
        dashboardUrl = '/superadmin/dashboard';
        break;
      case 'admin':
        dashboardUrl = `${prefix}/admin/dashboard`;
        break;
      case 'owner':
        dashboardUrl = `${prefix}/owner/dashboard`;
        break;
      case 'guard':
        dashboardUrl = `${prefix}/guard/dashboard`;
        break;
      case 'maintenance':
        dashboardUrl = '/maintenance/dashboard';
        break;
      default:
        dashboardUrl = '/login';
    }

    return dashboardUrl;
  };

  // Force reload when clicking dashboard link for owner role to ensure fresh data
  const handleDashboardClick = (e) => {
    if (currentUser?.role === 'owner') {
      e.preventDefault();
      // Force reload to ensure fresh data
      window.location.href = getDashboardUrl();
    }
  };

  // Check if a route is active
  const isActive = (path) => {
    // Use startsWith to match nested routes
    return location.pathname === path ||
      (path !== '/' && location.pathname.startsWith(path + '/'));
  };

  // Completely remove any dynamic style application for superadmin
  useEffect(() => {
    // Remove any inline styles from superadmin navigation items
    if (currentUser?.role === 'superadmin') {
      const removeInlineStyles = () => {
        const menuItems = document.querySelectorAll('.superadmin-menu-item');
        menuItems.forEach(item => {
          // Remove inline styles
          item.removeAttribute('style');

          // Also remove from children
          const children = item.querySelectorAll('*');
          children.forEach(child => {
            child.removeAttribute('style');
          });
        });
      };

      // Run initially and after any potential change
      removeInlineStyles();

      // Setup a MutationObserver to catch any dynamically added styles
      const observer = new MutationObserver(removeInlineStyles);
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['style'],
        subtree: true
      });

      return () => observer.disconnect();
    }
  }, [currentUser?.role]);

  return (
    <NavbarCSS currentUserRole={currentUser?.role}>
      <>
        <div className={`mobile-nav-toggle ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(!sidebarOpen)}>
          <i className={`fas ${sidebarOpen ? 'fa-times' : 'fa-bars'}`}></i>
        </div>

        <aside className={`sidebar-navigation ${sidebarOpen ? 'open' : ''} ${currentUser?.role === 'superadmin' ? 'superadmin-navigation' : ''}`}>
          <div className="sidebar-header">
            <Link to={getDashboardUrl()} className="logo-container" onClick={handleDashboardClick}>
              {currentUser?.role === 'superadmin' ? (
                // Fixed logo for superadmin
                <>
                  <img
                    src={defaultsettings.logo_url ? defaultsettings.logo_url : nivaasLogo}
                    alt="Nivaas Logo"
                    className="logo-image"
                  />

                </>
              ) : (settings.show_society_name === 'true' || settings.show_society_name === true || !settings.logo_url || logoError) ? (
                <div className="society-name">
                  {settings.society_name || getSocietyName(settings)}
                </div>
              ) : settings.logo_url && !logoError ? (
                <img
                  src={getLogoUrl(settings.logo_url)}
                  alt={getSocietyName(settings)}
                  className="logo-image"
                  onError={(e) => {
                    // console.log("Logo failed to load on render, showing society name instead");
                    e.target.style.display = 'none';
                    setLogoError(true);
                  }}
                  crossOrigin="anonymous"
                />
              ) : (
                <div className="society-name">
                  {settings.society_name || getSocietyName(settings)}
                </div>
              )}
            </Link>
          </div>

          {/* Welcome Header Section - Now below logo */}
          <div className="sidebar-welcome-header">
            <div className="welcome-message">
              <h6 className="welcome-text">Welcome!! {currentUser?.name || 'User'}</h6>
              {currentUser?.isSwitchedUser && (
                <div className="switch-user-indicator">
                  <small className="text-warning">
                    <i className="fas fa-user-secret mr-1"></i>
                    Switched User Mode
                  </small>
                </div>
              )}
            </div>
            <div className="user-actions">
              {/* Switch User button for Superadmin */}
              {currentUser?.role === 'superadmin' && (
                <button
                  className="user-action-link switch-user-link"
                  onClick={() => navigate('/superadmin/switch-user')}
                  type="button"
                  title="Switch User"
                >
                  <i className="fas fa-exchange-alt"></i>
                </button>
              )}
              {currentUser?.isSwitchedUser && (
                <button
                  className="user-action-link switch-user-management"
                  onClick={() => navigate('/superadmin/switch-user')}
                  type="button"
                  title="Switch User Management"
                >
                  <i className="fas fa-exchange-alt"></i>
                </button>
              )}
              <button
                className="user-action-link profile-link"
                onClick={handleProfileClick}
                type="button"
                title="Edit Profile"
              >
                <i className="fas fa-user-edit"></i>
              </button>
              <button
                className="user-action-link logout-link"
                onClick={handleLogout}
                type="button"
                title="Logout"
              >
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </div>
          </div>

          <nav className="sidebar-menu">
            <ul className="menu-items">
              {getMenuItems().map((item) => (
                <li key={item.path} className={`menu-item ${item.className || ''} ${isActive(item.path) ? 'active' : ''}`} data-path={item.path}>
                  <Link
                    to={item.path}
                    className="menu-link"
                    onClick={item.forceReload || item.path.includes('/dashboard') ? handleDashboardClick : null}
                  >
                    <i className={`${item.icon} menu-icon`}></i>
                    <span className="menu-text">{item.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="sidebar-footer">
            {/* <div className="organization-name-label">
              <small>{(settings.society_name && settings.society_name.trim()) || currentUser?.organization_name || (sessionStorage.getItem('user') ? JSON.parse(sessionStorage.getItem('user')).organization_name : null) || settings.organization_name || localStorage.getItem('organization_name') || 'Organization Name'}</small>
            </div> */}

            {/* Support Link for Admins */}
            {currentUser?.role === 'admin' && (
              <div className="menu-item">
                <Link to="/support" className="menu-link">
                  <i className="fas fa-headset me-1"></i>
                  Contact Support
                </Link>
              </div>
            )}

            {/* Privacy Policy Footer Link */}
            <div className="menu-item">
              <Link to="/privacy-policy" className="menu-link">
                <i className="fas fa-shield-alt me-1"></i>
                Privacy Policy
              </Link>
            </div>
          </div>
        </aside>
      </>
    </NavbarCSS>
  );
};

export default Navbar;